import { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient from '../../services/apiClient'
import { useAuthStore } from '../../store/authStore'
import { UserRole } from '../../types/auth'
import { toast } from 'react-toastify'

export type CourseWhiteboardItem = {
  id: number
  materialId: number
  courseId: number
  title: string
  diagramId: string
  pngUrl: string
  xmlUrl?: string
  isPublished?: number | boolean
  createdAt?: string
  materialTitle?: string
  materialType?: string
  sessionId?: number | null
  sessionTitle?: string | null
  sessionOrder?: number | null
}

type Props = {
  courseId: number
  refreshKey?: number
  onOpenMaterial?: (materialId: number) => void
}

const BROKEN_PNG =
  'data:image/svg+xml,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="200"><rect fill="#f1f5f9" width="100%" height="100%"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#94a3b8" font-size="14" font-family="sans-serif">Không tải được ảnh</text></svg>`
  )

function materialTypeLabel(type?: string): string {
  const t = String(type || '').toUpperCase()
  if (t === 'SLIDE') return 'Slide'
  if (t === 'REFERENCE') return 'Tham khảo'
  if (t === 'SAMPLE_PROJECT') return 'Dự án mẫu'
  return 'Khác'
}

export default function CourseWhiteboardGallery({
  courseId,
  refreshKey = 0,
  onOpenMaterial,
}: Props) {
  const user = useAuthStore((s) => s.user)
  const isAdmin = Boolean(
    user?.roles?.includes(UserRole.ADMIN) ||
      (user?.roles as string[] | undefined)?.includes('SUPER_ADMIN')
  )
  const [items, setItems] = useState<CourseWhiteboardItem[]>([])
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [sessionFilter, setSessionFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [preview, setPreview] = useState<CourseWhiteboardItem | null>(null)

  const load = useCallback(async () => {
    if (!courseId) return
    setLoading(true)
    try {
      const url = isAdmin
        ? `/admin/courses/${courseId}/whiteboards`
        : `/student/courses/${courseId}/whiteboards`
      const res = await apiClient.get(url)
      setItems(Array.isArray(res.data?.data) ? res.data.data : [])
    } catch (e: any) {
      setItems([])
      toast.error(e?.response?.data?.message || 'Không tải được bảng minh họa')
    } finally {
      setLoading(false)
    }
  }, [courseId, isAdmin])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const sessions = useMemo(() => {
    const map = new Map<number, { id: number; title: string; order: number }>()
    for (const item of items) {
      if (item.sessionId == null) continue
      if (!map.has(item.sessionId)) {
        map.set(item.sessionId, {
          id: item.sessionId,
          title: item.sessionTitle || `Buổi ${item.sessionOrder ?? item.sessionId}`,
          order: Number(item.sessionOrder ?? 9999),
        })
      }
    }
    return [...map.values()].sort((a, b) => a.order - b.order)
  }, [items])

  const types = useMemo(() => {
    const set = new Set<string>()
    for (const item of items) {
      const t = String(item.materialType || 'OTHER').toUpperCase()
      set.add(t)
    }
    return [...set]
  }, [items])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items.filter((item) => {
      if (sessionFilter === 'none' && item.sessionId != null) return false
      if (sessionFilter !== 'all' && sessionFilter !== 'none' && String(item.sessionId) !== sessionFilter) {
        return false
      }
      if (typeFilter !== 'all' && String(item.materialType || '').toUpperCase() !== typeFilter) {
        return false
      }
      if (!q) return true
      const hay = `${item.title} ${item.materialTitle || ''} ${item.sessionTitle || ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [items, query, sessionFilter, typeFilter])

  if (loading && items.length === 0) {
    return (
      <p className="text-sm text-m2-on-surface-variant">Đang tải bảng minh họa…</p>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Tìm theo tên bảng hoặc tài liệu…"
          className="flex-1 rounded-lg border border-m2-outline-variant bg-white px-3 py-2 text-sm text-m2-on-surface"
        />
        <select
          value={sessionFilter}
          onChange={(e) => setSessionFilter(e.target.value)}
          className="rounded-lg border border-m2-outline-variant bg-white px-3 py-2 text-sm text-m2-on-surface"
        >
          <option value="all">Tất cả buổi</option>
          <option value="none">Toàn khóa (không gắn buổi)</option>
          {sessions.map((s) => (
            <option key={s.id} value={String(s.id)}>
              Buổi {s.order}: {s.title}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-m2-outline-variant bg-white px-3 py-2 text-sm text-m2-on-surface"
        >
          <option value="all">Tất cả loại tài liệu</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {materialTypeLabel(t)}
            </option>
          ))}
        </select>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-m2-on-surface-variant">
          Khóa học chưa có bảng minh họa. Giáo viên lưu bảng trắng trên slide thì ảnh sẽ hiện ở đây.
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-m2-on-surface-variant">Không có bảng nào khớp bộ lọc.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map((wb) => (
            <article
              key={wb.id}
              className="rounded-lg border border-m2-outline-variant bg-m2-surface-low overflow-hidden flex flex-col"
            >
              <button
                type="button"
                className="block w-full text-left"
                onClick={() => setPreview(wb)}
              >
                <img
                  src={wb.pngUrl}
                  alt={wb.title}
                  className="w-full aspect-[4/3] object-cover bg-white"
                  onError={(e) => {
                    e.currentTarget.onerror = null
                    e.currentTarget.src = BROKEN_PNG
                  }}
                />
              </button>
              <div className="p-2.5 flex flex-col gap-1 min-h-0">
                <h4 className="text-sm font-semibold text-m2-on-surface line-clamp-2">{wb.title}</h4>
                <p className="text-[11px] text-m2-on-surface-variant line-clamp-1">
                  {wb.materialTitle || 'Tài liệu'}
                  {wb.sessionTitle
                    ? ` · Buổi ${wb.sessionOrder ?? ''} ${wb.sessionTitle}`
                    : ' · Toàn khóa'}
                </p>
                {onOpenMaterial && (
                  <button
                    type="button"
                    className="mt-1 text-xs font-medium text-m2-primary hover:underline self-start"
                    onClick={() => onOpenMaterial(wb.materialId)}
                  >
                    Mở tài liệu
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="font-semibold text-m2-on-surface">{preview.title}</h3>
                <p className="text-xs text-m2-on-surface-variant mt-0.5">
                  {preview.materialTitle}
                  {preview.sessionTitle ? ` · ${preview.sessionTitle}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {onOpenMaterial && (
                  <button
                    type="button"
                    className="text-sm text-m2-primary hover:underline"
                    onClick={() => {
                      onOpenMaterial(preview.materialId)
                      setPreview(null)
                    }}
                  >
                    Mở tài liệu
                  </button>
                )}
                <button
                  type="button"
                  className="text-sm text-m2-primary hover:underline"
                  onClick={() => setPreview(null)}
                >
                  Đóng
                </button>
              </div>
            </div>
            <img
              src={preview.pngUrl}
              alt={preview.title}
              className="w-full h-auto rounded border"
              onError={(e) => {
                e.currentTarget.onerror = null
                e.currentTarget.src = BROKEN_PNG
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
